import { Component, computed, effect, inject, signal } from "@angular/core"
import { httpResource } from "@angular/common/http"
import { Router } from "@angular/router"
import { ToastService } from "src/app/services"
import { Column, PagedSurvey, TableLazyLoadEvent } from "../../models"
import { SurveyService } from "../../services"
import { toPagedParams } from "../../helpers/TablePaging"
import { TableComponent } from "../../components/table/table.component"

@Component({
    selector: "valben-surveys-table",
    templateUrl: "./surveys-table.page.html",
    styles: [`
        :host {
            width: 100%;
            flex: 1 1 auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
        }
    `],
    standalone: true,
    imports: [TableComponent],
})
export class SurveysTablePage {
    private _surveyService = inject(SurveyService)
    private _messageService = inject(ToastService)
    private _router = inject(Router)

    cols: Column[] = [
        { field: "names", subField: "hr", header: "Naslov", bodyType: "langs", filterType: "text", filterMode: "contains", pairWith: "descriptions" },
        { field: "descriptions", subField: "hr", header: "Opis", bodyType: "langs", filterType: "text", filterMode: "contains" },
        { field: "createdByUser", header: "Kreiranje", bodyType: "text", filterType: "text", filterMode: "contains", pairWith: "modifiedByUser" },
        { field: "modifiedByUser", header: "Zadnja izmjena", bodyType: "text", filterType: "text", filterMode: "contains", prefix: "↳" },
        { field: "createdDate", header: "Datum kreiranja", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "modifiedDate" },
        { field: "modifiedDate", header: "Datum izmjene", bodyType: "datetime", filterType: "date", filterMode: "dateIs", sortable: true },
        { field: "startDate", header: "Datum početka", bodyType: "date", filterType: "date", filterMode: "dateIs", sortable: true, pairWith: "endDate" },
        { field: "endDate", header: "Datum završetka", bodyType: "date", filterType: "date", filterMode: "dateIs", sortable: true, prefix: "→" },
        { field: "isActive", header: "Aktivan", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Aktivna", falseText: "Neaktivna", pairWith: "isDraft" },
        { field: "isDraft", header: "Skica", bodyType: "boolean", filterType: "boolean", sortable: true, trueText: "Skica", falseText: "Bez skice" },
        { field: "link", header: "Link", bodyType: "link", filterType: "text", filterMode: "contains" },
        { field: "version", header: "Verzija", bodyType: "number", filterType: "numeric", sortable: true, prefix: "v" },
        { field: "imageUrl", header: "Slika", bodyType: "file", icon: "image" },
    ]

    private _pageRequest = signal<TableLazyLoadEvent | undefined>(undefined)

    surveys = httpResource<PagedSurvey>(() => {
        const event = this._pageRequest()
        return event ? this._surveyService.PagedRequest(toPagedParams(event, this.cols)) : undefined
    })

    tableValues = computed(() =>
        (this.surveys.value()?.items ?? []).map(survey => ({
            ...survey,
            createdDate: new Date(survey.createdDate),
            modifiedDate: new Date(survey.modifiedDate),
            startDate: survey.startDate ? new Date(survey.startDate) : null,
            endDate: survey.endDate ? new Date(survey.endDate) : null,
        }))
    )

    totalRecords = computed(() => this.surveys.value()?.totalCount ?? 0)
    loading = computed(() => this.surveys.isLoading())

    refresh = () => this.surveys.reload()

    constructor() {
        effect(() => {
            if (!this.surveys.error()) return
            this._messageService.add({
                severity: "error",
                summary: "Greška",
                detail: "Došlo je do pogreške prilikom dohvata anketa.",
                life: 10000,
            })
        })
    }

    loadPage(event: TableLazyLoadEvent): void {
        setTimeout(() => this._pageRequest.set(event))
    }

    add(): void {
        this._router.navigate(["/surveys/add"])
    }

    edit(masterId: string): void {
        this._router.navigate(["/surveys/edit", masterId])
    }
}
